import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import Button from '../../../components/common/Button';
import Input from '../../../components/common/Input';
import { 
  Mail, 
  Lock, 
  Building2, 
  ArrowLeft, 
  CheckCircle, 
  AlertCircle,
  ShieldCheck
} from 'lucide-react';

const ForgotPassword = () => {
  const [isResetMode, setIsResetMode] = useState(false); // true if user came from reset link
  const [formData, setFormData] = useState({
    email: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  
  const { forgotPassword, resetPasswordWithCode, isOnline } = useAuth();
  const { addNotification } = useNotifications();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if user came from reset link
  useEffect(() => {
    const mode = searchParams.get('mode');
    const actionCode = searchParams.get('oobCode');
    
    if (mode === 'resetPassword' && actionCode) {
      // Store the action code in sessionStorage
      sessionStorage.setItem('resetCode', actionCode);
      setIsResetMode(true);
      
      // Extract email from reset code
      try {
        const emailFromCode = decodeURIComponent(window.location.search.split('email=')[1] || '');
        if (emailFromCode) {
          setResetEmail(emailFromCode);
          setFormData(prev => ({ ...prev, email: emailFromCode }));
        }
      } catch (error) {
        console.log('Could not extract email from URL');
      }
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
    
    if (errors.general) {
      setErrors(prev => ({
        ...prev,
        general: null
      }));
    }
  };

  const validateEmailStep = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    return newErrors;
  };

  const validatePasswordStep = () => {
    const newErrors = {};
    
    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      newErrors.newPassword = 'Include uppercase, lowercase, and number';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    return newErrors;
  };

  const handleSendResetEmail = async () => {
    if (!isOnline) {
      setErrors({ general: 'No internet connection. Please check your network.' });
      addNotification({
        type: 'error',
        title: 'Network Error',
        message: 'Please check your internet connection'
      });
      return;
    }
    
    const validationErrors = validateEmailStep();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setLoading(true);
    setErrors({});
    setSuccessMessage('');
    
    try {
      const result = await forgotPassword(formData.email);
      
      if (result.success) {
        setSuccessMessage(result.message);
        addNotification({
          type: 'success',
          title: 'Reset Email Sent',
          message: 'Check your email for the password reset link.'
        });
        
        // Clear form
        setFormData({ email: '', newPassword: '', confirmPassword: '' });
      } else {
        setErrors({ general: result.message });
        addNotification({
          type: 'error',
          title: 'Failed to Send Email',
          message: result.message
        });
      }
    } catch (error) {
      console.error('Error sending reset email:', error);
      setErrors({ general: 'Failed to send reset email. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!isOnline) {
      setErrors({ general: 'No internet connection. Please check your network.' });
      return;
    }
    
    const validationErrors = validatePasswordStep();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setLoading(true);
    setErrors({});
    
    try {
      // Get the reset code from sessionStorage or URL
      let resetCode = sessionStorage.getItem('resetCode');
      if (!resetCode) {
        resetCode = searchParams.get('oobCode');
      }
      
      if (!resetCode) {
        throw new Error('Reset link is invalid or expired. Please request a new reset link.');
      }
      
      const result = await resetPasswordWithCode(resetCode, formData.newPassword);
      
      if (result.success) {
        // Clear the reset code from session storage
        sessionStorage.removeItem('resetCode');
        
        setSuccessMessage(result.message);
        addNotification({
          type: 'success',
          title: 'Password Reset Successful',
          message: 'You can now login with your new password.'
        });
        
        // Auto-redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      } else {
        setErrors({ general: result.message });
        addNotification({
          type: 'error',
          title: 'Reset Failed',
          message: result.message
        });
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setErrors({ general: error.message || 'Failed to reset password. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const renderResetForm = () => {
    if (isResetMode) {
      // Password Reset Form (when user clicks email link)
      return (
        <div className="space-y-6">
          <div className="text-center mb-4">
            <div className="flex justify-center mb-4">
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <Lock className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Create New Password</h2>
            <p className="mt-2 text-gray-600">
              {resetEmail ? `For account: ${resetEmail}` : 'Enter your new password below'}
            </p>
          </div>
          
          {errors.general && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Error</p>
                  <p className="text-sm text-red-700 mt-0.5">{errors.general}</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  value={formData.newPassword}
                  onChange={handleChange}
                  error={!!errors.newPassword}
                  placeholder="Enter your new password"
                  className="pl-10 py-3 text-base"
                  disabled={loading}
                />
              </div>
              {errors.newPassword && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.newPassword}
                </p>
              )}
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={!!errors.confirmPassword}
                  placeholder="Re-enter your new password"
                  className="pl-10 py-3 text-base"
                  disabled={loading}
                />
              </div>
              {errors.confirmPassword && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.confirmPassword}
                </p>
              )}
            </div>
          </div>
          
          {/* Password Requirements */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs font-semibold text-blue-800 mb-2">Password Requirements:</p>
            <div className="space-y-1">
              <div className="flex items-center">
                <CheckCircle className={`h-3 w-3 mr-2 ${formData.newPassword.length >= 6 ? 'text-green-500' : 'text-gray-400'}`} />
                <span className={`text-xs ${formData.newPassword.length >= 6 ? 'text-green-700' : 'text-gray-600'}`}>
                  At least 6 characters
                </span>
              </div>
              <div className="flex items-center">
                <CheckCircle className={`h-3 w-3 mr-2 ${/[A-Z]/.test(formData.newPassword) ? 'text-green-500' : 'text-gray-400'}`} />
                <span className={`text-xs ${/[A-Z]/.test(formData.newPassword) ? 'text-green-700' : 'text-gray-600'}`}>
                  One uppercase letter
                </span>
              </div>
              <div className="flex items-center">
                <CheckCircle className={`h-3 w-3 mr-2 ${/[a-z]/.test(formData.newPassword) ? 'text-green-500' : 'text-gray-400'}`} />
                <span className={`text-xs ${/[a-z]/.test(formData.newPassword) ? 'text-green-700' : 'text-gray-600'}`}>
                  One lowercase letter
                </span>
              </div>
              <div className="flex items-center">
                <CheckCircle className={`h-3 w-3 mr-2 ${/\d/.test(formData.newPassword) ? 'text-green-500' : 'text-gray-400'}`} />
                <span className={`text-xs ${/\d/.test(formData.newPassword) ? 'text-green-700' : 'text-gray-600'}`}>
                  One number
                </span>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <Button
              type="button"
              variant="primary"
              size="large"
              fullWidth
              loading={loading}
              onClick={handleResetPassword}
              className="py-3 text-base font-medium shadow-md hover:shadow-lg transition-all duration-200"
              disabled={loading || !isOnline}
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </Button>
            
            <Button
              type="button"
              variant="outline"
              size="large"
              fullWidth
              onClick={() => navigate('/login')}
              className="py-3 text-base font-medium"
              disabled={loading}
            >
              <ArrowLeft className="h-4 w-4 mr-2 inline-block" />
              Back to Login
            </Button>
          </div>
        </div>
      );
    }
    
    // Email Request Form (default)
    return (
      <div className="space-y-6">
        <div className="text-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Reset Your Password</h2>
          <p className="mt-2 text-gray-600">
            Enter your email address and we'll send you a reset link
          </p>
        </div>
        
        {successMessage && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-800">Success!</p>
                <p className="text-sm text-green-700 mt-0.5">{successMessage}</p>
              </div>
            </div>
          </div>
        )}
        
        {errors.general && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-700 mt-0.5">{errors.general}</p>
              </div>
            </div>
          </div>
        )}
        
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-400" />
            </div>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              placeholder="Enter your email address"
              className="pl-10 py-3 text-base"
              disabled={loading}
            />
          </div>
          {errors.email && (
            <p className="mt-2 text-sm text-red-600 flex items-center">
              <AlertCircle className="h-4 w-4 mr-1" />
              {errors.email}
            </p>
          )}
        </div>
        
        <div className="space-y-4">
          <Button
            type="button"
            variant="primary"
            size="large"
            fullWidth
            loading={loading}
            onClick={handleSendResetEmail}
            className="py-3 text-base font-medium shadow-md hover:shadow-lg transition-all duration-200"
            disabled={loading || !isOnline}
          >
            {loading ? 'Sending Reset Link...' : 'Send Reset Link'}
          </Button>
          
          <Button
            type="button"
            variant="outline"
            size="large"
            fullWidth
            onClick={() => navigate('/login')}
            className="py-3 text-base font-medium"
            disabled={loading}
          >
            <ArrowLeft className="h-4 w-4 mr-2 inline-block" />
            Back to Login
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <div className="h-16 w-16 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 flex items-center justify-center shadow-lg">
              <Building2 className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
            {isResetMode ? 'Reset Password' : 'Password Recovery'}
          </h1>
        </div>

        <div className="bg-white py-8 px-6 shadow-lg rounded-2xl sm:px-10 border border-gray-200">
          {/* Form Content */}
          {renderResetForm()}
          
          {/* Network Status */}
          {!isOnline && (
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center">
              <AlertCircle className="h-5 w-5 text-yellow-600 mr-3 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-800">You are offline</p>
                <p className="text-xs text-yellow-700 mt-0.5">
                  Password reset requires internet connection.
                </p>
              </div>
            </div>
          )}
          
          {/* Help Text */}
          {!isResetMode && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Need help?{' '}
                <a 
                  href="mailto:support@crossdeal.com" 
                  className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
                >
                  Contact Support
                </a>
              </p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            Remember your password?{' '}
            <Link 
              to="/login" 
              className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
            >
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;